import { FormattedMessage } from 'react-intl';

import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';

import { widget as ShowDetails } from '#src/components/TokenPreview/ShowDetails/index.tsx';
import { RangeSlider as ConnectedRangeSlider } from '#src/search/RangeSlider/index.tsx';
import { makeRangeFilter } from '#src/searchCards/RangeSlider/index.tsx';
import { RefineBackendCardPlayerAge } from '#src/searchCards/RefineAge/index.tsx';
import { RefineBackendAppearances } from '#src/searchCards/RefineAppearances/index.tsx';
import { RefineBackendAverageScore } from '#src/searchCards/RefineAverageScore/index.tsx';
import {
  RefineActiveClub,
  RefineBackendActiveClub,
  RefineBackendClub,
  RefineClub,
} from '#src/searchCards/RefineClubs/index.tsx';
import {
  RefineBackendCardEdition,
  RefineBackendNationality,
  RefineBackendPlayer,
  RefineBackendSeason,
} from '#src/searchCards/RefineList/index.tsx';
import { RefineBackendAnyPosition } from '#src/searchCards/RefinePosition/index.tsx';
import {
  AcceptedCurrencyGroup,
  BackendAcceptedCurrencyGroup,
  BackendDailyFilter,
  BackendNotInLineupFilter,
  BackendRefineInSeason,
  BackendRefineOnSale,
  BackendWatchlistGroup,
  BidToEarnFilter,
  DailyFilter,
  NotInLineupFilter,
  PriceGroup,
  PromotionalEventFilter,
  PromotionalEventFilterForInstantBuy,
  RarityGroup,
  RefineAppearances,
  RefineBackendCardLevel,
  RefineBackendSerialNumber,
  RefineCardEdition,
  RefineCardLevel,
  RefineCardPlayerAge,
  RefineCreditEligible,
  RefineInSeason,
  RefineNationality,
  RefineOnSale,
  RefinePlayer,
  RefinePosition,
  RefineSerialNumber,
  RefineSingleYearSeason,
  filterSeparator,
} from '#src/searchCards/index.ts';
import { WatchlistGroup } from '#src/searchCards/refineGroups.tsx';

import { RefineTenGameAverageScore } from '../nba/cardFilters.tsx';
import { backendWidget as BackendBaseballCustomDecksFilter } from './BaseballCustomDecksFilter/BackendBaseballCustomDecksFilter.tsx';
import { widget as BaseballCustomDecksFilter } from './BaseballCustomDecksFilter/index.tsx';
import {
  BackendRefineMLBPosition,
  RefineMLBPosition,
} from './RefineMLBPosition/index.tsx';
import {
  backendWidget as BackendRefineProbablePitchers,
  widget as RefineProbablePitchers,
} from './RefineProbablePitchers/index.tsx';

const MAX_TEAM_LIMIT = 30;

const RefineSeasonAverageScore = makeRangeFilter(({ visible }) => {
  return (
    <ConnectedRangeSlider
      attribute={FILTERS.mlbSeasonAverageScore.attribute}
      visible={visible}
    />
  );
}, FILTERS.mlbSeasonAverageScore);

const InSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: RefineInSeason,
  filters: [],
};

const CardGroup = {
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    RefineMLBPosition,
    RefineSingleYearSeason,
    RefineClub(MAX_TEAM_LIMIT),
  ],
};

export const MatchInfoGroup = (
  withSeasonScore: boolean,
  props?: {
    withoutProbablePitchers?: boolean;
  }
) => ({
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [
    DailyFilter,
    withSeasonScore ? RefineSeasonAverageScore : RefineTenGameAverageScore,
    RefineAppearances(),
    ...((props?.withoutProbablePitchers && []) || [RefineProbablePitchers]),
  ],
});

export const PlayerGroup = (props?: {
  withoutActiveClub?: boolean;
  withClub?: boolean;
}) => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefinePosition(),
    RefineCardPlayerAge,
    RefineNationality,
    RefinePlayer,
    ...((props?.withoutActiveClub && []) || [RefineActiveClub(MAX_TEAM_LIMIT)]),
    ...((props?.withClub && [RefineClub(MAX_TEAM_LIMIT)]) || []),
  ],
});

const CollectibleGroup = (props?: { withoutCardLevel?: boolean }) => ({
  slug: 'collectibles',
  title: <FormattedMessage {...cardAttributes.collectibles} />,
  filters: [
    RefineSerialNumber,
    ...((props?.withoutCardLevel && []) || [RefineCardLevel]),
    RefineCardEdition,
  ],
});

const ToggleGroup = (filters: FilterWidget[]) => ({
  slug: 'toggle',
  title: null,
  filters: [
    ...(filters.flatMap((filter, i) => [
      ...((i && [filterSeparator]) || []),
      filter,
    ]) || []),
  ],
});

export const primaryMarketFilters = (withSeasonScore: boolean) => [
  RarityGroup,
  {
    slug: 'toggle',
    title: null,
    filters: [PromotionalEventFilter],
  },
  PriceGroup,
  ToggleGroup([BidToEarnFilter()]),
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore),
  PlayerGroup(),
  CollectibleGroup({ withoutCardLevel: true }),
];

export const instantBuyMarketFilters = (withSeasonScore: boolean) => [
  RarityGroup,
  {
    slug: 'toggle',
    title: null,
    filters: [PromotionalEventFilterForInstantBuy],
  },
  PriceGroup,
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore, { withoutProbablePitchers: true }),
  PlayerGroup(),
  CollectibleGroup({ withoutCardLevel: true }),
];

export const secondaryMarketFilters = (withSeasonScore: boolean) =>
  [
    InSeasonFilter,
    RarityGroup,
    PriceGroup,
    WatchlistGroup,
    CardGroup,
    MatchInfoGroup(withSeasonScore),
    PlayerGroup(),
    CollectibleGroup(),
    AcceptedCurrencyGroup,
  ].filter(Boolean);

export const secondaryMarketStackedFilters = [
  PriceGroup,
  CardGroup,
  CollectibleGroup(),
  AcceptedCurrencyGroup,
].filter(Boolean);

export const playerPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  CardGroup,
  MatchInfoGroup(false, { withoutProbablePitchers: true }),
  CollectibleGroup(),
  AcceptedCurrencyGroup,
];

export const clubPageFilters = (withSeasonScore: boolean) => [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup,
  MatchInfoGroup(withSeasonScore),
  PlayerGroup({ withoutActiveClub: false }),
  CollectibleGroup(),
  AcceptedCurrencyGroup,
];

export const myClubFilters = ({
  withSeasonScore,
  showDecksFilter,
}: {
  withSeasonScore: boolean;
  showDecksFilter: boolean;
}) =>
  [
    { slug: 'show-details', filters: [ShowDetails] },
    InSeasonFilter,
    ToggleGroup([RefineOnSale(), NotInLineupFilter]),
    ...((showDecksFilter && [
      { slug: 'decks', filters: [BaseballCustomDecksFilter] },
    ]) ||
      []),
    WatchlistGroup,
    CardGroup,
    MatchInfoGroup(withSeasonScore),
    PlayerGroup(),
    CollectibleGroup(),
    AcceptedCurrencyGroup,
  ].filter(Boolean);

export const tradeFilters = [
  InSeasonFilter,
  RarityGroup,
  { slug: 'not-in-lineup', filters: [NotInLineupFilter] },
  WatchlistGroup,
  {
    slug: 'deck',
    filters: [BaseballCustomDecksFilter],
  },
  CardGroup,
];

const BackendInSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: BackendRefineInSeason,
  filters: [],
};

const BackendCardGroup = {
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    BackendRefineMLBPosition,
    RefineBackendSeason,
    RefineBackendClub(MAX_TEAM_LIMIT),
  ],
};

const BackendMatchInfoGroup = {
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [
    BackendDailyFilter,
    RefineBackendAverageScore({ disableFiveAverageScore: true }),
    RefineBackendAppearances(),
    BackendRefineProbablePitchers,
  ],
};

const BackendPlayerGroup = () => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefineBackendAnyPosition(),
    RefineBackendCardPlayerAge,
    RefineBackendNationality,
    RefineBackendPlayer,
    RefineBackendActiveClub(MAX_TEAM_LIMIT),
  ],
});

export const backendMyMLBClubFilters = ({
  showDecksFilter = true,
}: {
  showDecksFilter?: boolean;
} = {}) =>
  [
    { slug: 'show-details', filters: [ShowDetails] },
    BackendInSeasonFilter,
    ToggleGroup([BackendRefineOnSale(), BackendNotInLineupFilter]),
    ...((showDecksFilter && [
      { slug: 'decks', filters: [BackendBaseballCustomDecksFilter] },
    ]) ||
      []),
    BackendWatchlistGroup,
    BackendCardGroup,
    BackendMatchInfoGroup,
    BackendPlayerGroup(),
    {
      slug: 'collectibles',
      title: <FormattedMessage {...cardAttributes.collectibles} />,
      filters: [
        RefineBackendSerialNumber,
        RefineBackendCardLevel,
        RefineBackendCardEdition,
      ],
    },
    BackendAcceptedCurrencyGroup,
  ].filter(Boolean);

export const backendMyMLBVaultFilters = [
  { slug: 'show-details', filters: [ShowDetails] },
  BackendInSeasonFilter,
  BackendCardGroup,
  BackendPlayerGroup(),
  {
    slug: 'collectibles',
    title: <FormattedMessage {...cardAttributes.collectibles} />,
    filters: [
      RefineBackendSerialNumber,
      RefineBackendCardLevel,
      RefineBackendCardEdition,
    ],
  },
];
